import React, { useEffect, useState } from 'react';
import { SupportWriteGuard } from '../../context/SupportSessionContext';
import {
    DEFAULT_PREVIEW_LINES,
    normalizeCommunicationsSettings,
    PREVIEW_LINE_OPTIONS,
    STATUS_CATEGORY_LABELS,
} from '../../lib/communicationsSettings';
import HoverHelp from '../shared/HoverHelp';
import GroupCommunicationsSettings from './GroupCommunicationsSettings';


function SettingsSection({ title, children, dark = false, help = null }) {
    return (
        <section className={`space-y-3 border-b pb-4 last:border-b-0 last:pb-0 ${dark ? 'border-gray-700' : 'border-gray-200'}`} aria-label={title}>
            <h3 className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide">
                <span>{title}</span>
                {help}
            </h3>
            {children}
        </section>
    );
}

function CommunicationsSettingsModal({
    settings,
    onClose,
    onSave,
    saving = false,
    error = '',
    theme = 'light',
    userId,
    previewLines = DEFAULT_PREVIEW_LINES,
    onPreviewLinesChange,
    canChangeNumberCheckLimit = false,
    // Comms final pass: Group communications (its own table and Save; admin-only to change).
    groupSettingsClient = null,
    orgId = null,
    canChangeGroupSettings = false,
    onGroupSettingsSaved,
}) {
    const dark = theme === 'dark';
    const [draft, setDraft] = useState(() => normalizeCommunicationsSettings(settings));

    useEffect(() => {
        setDraft(normalizeCommunicationsSettings(settings));
    }, [settings]);

    const [draggedStatus, setDraggedStatus] = useState(null);
    const modalCls = dark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-800';
    const labelCls = dark ? 'text-gray-300' : 'text-gray-700';
    const hintCls = dark ? 'text-gray-400' : 'text-gray-500';
    const inputCls = dark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300';
    const segmentBase = `ia-hover-pulse rounded-lg border px-2 py-1 text-xs font-medium ${dark ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-50'}`;
    const segmentActive = 'ia-selected-pill';
    const rowCls = `flex items-center justify-between gap-3 text-sm ${labelCls}`;

    const moveStatus = (status, offset) => {
        setDraft((current) => {
            const order = current.statusCategoryOrder.slice();
            const index = order.indexOf(status);
            const nextIndex = index + offset;
            if (index < 0 || nextIndex < 0 || nextIndex >= order.length) return current;
            const [item] = order.splice(index, 1);
            order.splice(nextIndex, 0, item);
            return { ...current, statusCategoryOrder: order };
        });
    };

    const dropStatus = (targetStatus) => {
        if (!draggedStatus || draggedStatus === targetStatus) return;
        setDraft((current) => {
            const order = current.statusCategoryOrder.filter((status) => status !== draggedStatus);
            const targetIndex = order.indexOf(targetStatus);
            if (targetIndex < 0) return current;
            order.splice(targetIndex, 0, draggedStatus);
            return { ...current, statusCategoryOrder: order };
        });
        setDraggedStatus(null);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="presentation">
            <div className={`flex max-h-[calc(100vh-2rem)] w-full max-w-md flex-col rounded-xl border shadow-xl ${modalCls}`} role="dialog" aria-modal="true" aria-label="Communications settings">
                <div className={`shrink-0 flex items-center justify-between gap-3 border-b px-4 py-3 ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div>
                        <h2 className="text-base font-semibold">Communications settings</h2>
                    </div>
                    <button type="button" onClick={onClose} className={`text-lg leading-none ${hintCls}`} aria-label="Close settings">✕</button>
                </div>
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
                    {canChangeNumberCheckLimit && <label className={rowCls}>Waiting for reconnection after (minutes)
                        <input type="number" aria-label="Reconnection threshold in minutes" min="1" max="1440" value={draft.reconnectionMinutes}
                            onChange={e => setDraft(current => ({ ...current, reconnectionMinutes: e.target.value }))} className={`w-20 rounded border p-2 ${inputCls}`} />
                    </label>}
                    <SettingsSection title="Appearance" dark={dark}>
                        {/* BL-177 Step 2d (section 2): flag and bubble colours follow the shared colour system — one meaning per
                            colour, always with words — so they are no longer picked per company. Saved values are kept, unused. */}
                        <p className="text-sm" data-colour-system-note="true">
                            Flag and message colours follow IntelAgent's colour system (red = act now, orange = check this, yellow = someone's on it, blue = come back to it, purple = time-based, green = done, grey = no action; teal = us, slate = them), so they look the same for everyone.
                        </p>
                    </SettingsSection>
                    <SettingsSection title="New contacts" dark={dark}>
                        <div className={rowCls}>
                            <span className="inline-flex items-center">
                                <span>New contact handling</span>
                                <HoverHelp helpId="communications.new-contact-handling" userId={userId} />
                            </span>
                            <span className="flex items-center gap-1.5">
                                {[
                                    ['ask', 'Ask before saving'],
                                    ['autosave', 'Auto-save silently'],
                                ].map(([value, label]) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setDraft((current) => ({ ...current, newContactHandling: value }))}
                                        className={`${segmentBase} ${draft.newContactHandling === value ? segmentActive : ''}`}
                                        aria-pressed={draft.newContactHandling === value}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </span>
                        </div>
                    </SettingsSection>
                    <SettingsSection title="Display" dark={dark}>
                        {/* BL-177 Gate 17b: moved here from the gear on the conversation list.
                            Per user, this browser only, and applies right away (not on Save). */}
                        <div className={rowCls}>
                            <span className="flex flex-col">
                                <span>Message preview</span>
                                <span className={`text-[11px] ${hintCls}`}>Just for you · applies right away</span>
                            </span>
                            <span className="flex flex-wrap items-center justify-end gap-1.5" role="radiogroup" aria-label="Message preview">
                                {PREVIEW_LINE_OPTIONS.map(([value, label]) => (
                                    <button
                                        key={value}
                                        type="button"
                                        role="radio"
                                        aria-checked={previewLines === value}
                                        onClick={() => onPreviewLinesChange?.(value)}
                                        className={`${segmentBase} ${previewLines === value ? segmentActive : ''}`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </span>
                        </div>
                        <div className={rowCls}>
                            <span>Sort by</span>
                            <span className="flex items-center gap-1.5">
                                {[
                                    ['recent', 'Recent activity'],
                                    ['first_name', 'First name'],
                                    ['last_name', 'Last name'],
                                ].map(([value, label]) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setDraft((current) => ({ ...current, threadSortOrder: value }))}
                                        className={`${segmentBase} ${draft.threadSortOrder === value ? segmentActive : ''}`}
                                        aria-pressed={draft.threadSortOrder === value}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </span>
                        </div>
                        <label className={rowCls}>
                            <span>Show Subject line</span>
                            <input
                                type="checkbox"
                                checked={draft.allowSubjectLine}
                                disabled={saving}
                                onChange={(event) => setDraft((current) => ({ ...current, allowSubjectLine: event.target.checked }))}
                                aria-label="Show Subject line"
                            />
                        </label>
                        <label className={rowCls}>
                            <span>
                                Check numbers automatically
                                <span className="block text-xs font-normal opacity-90">When a phone number is first added or used, ask Twilio if it is a mobile, a landline or VoIP (about $0.008 each, re-checked once a year). Landlines can't get texts.</span>
                            </span>
                            <input
                                type="checkbox"
                                checked={draft.checkNumbersAutomatically}
                                disabled={saving}
                                onChange={(event) => setDraft((current) => ({ ...current, checkNumbersAutomatically: event.target.checked }))}
                                aria-label="Check numbers automatically"
                            />
                        </label>
                        <label className={rowCls}>
                            <span>
                                Number checks per day
                                <span className="block text-xs font-normal opacity-90">The most numbers checked with Twilio in one day, in Test and Live (automatic and "Check now"). Admins get one alert when it is reached. 0 = none.</span>
                            </span>
                            <input
                                type="number"
                                min={0}
                                max={10000}
                                value={draft.numberCheckDailyLimit}
                                disabled={saving || !canChangeNumberCheckLimit}
                                title={canChangeNumberCheckLimit ? undefined : 'Only an Owner or Tenant Admin can change this.'}
                                onChange={(event) => setDraft((current) => ({ ...current, numberCheckDailyLimit: event.target.value }))}
                                className={`w-20 rounded border px-2 py-1 text-xs ${inputCls}`}
                                aria-label="Number checks per day"
                            />
                        </label>
                        <label className={rowCls}>
                            <span>Group threads by account</span>
                            <input
                                type="checkbox"
                                checked={draft.groupThreadsByAccount}
                                disabled={saving}
                                onChange={(event) => setDraft((current) => ({ ...current, groupThreadsByAccount: event.target.checked }))}
                                aria-label="Group threads by account"
                            />
                        </label>
                        <label className={rowCls}>
                            <span>Channel active hours (green)</span>
                            <input
                                type="number"
                                min={1}
                                max={72}
                                value={draft.channelActiveHours}
                                onChange={(event) => setDraft((current) => ({ ...current, channelActiveHours: event.target.value }))}
                                className={`w-20 rounded border px-2 py-1 text-xs ${inputCls}`}
                                aria-label="Channel active hours"
                            />
                        </label>
                        <label className={rowCls}>
                            <span>Channel cooling hours (amber→gray)</span>
                            <input
                                type="number"
                                min={2}
                                max={168}
                                value={draft.channelCoolingHours}
                                onChange={(event) => setDraft((current) => ({ ...current, channelCoolingHours: event.target.value }))}
                                className={`w-20 rounded border px-2 py-1 text-xs ${inputCls}`}
                                aria-label="Channel cooling hours"
                            />
                        </label>
                        <label className={rowCls}>
                            <span>Handoff nudge minutes</span>
                            <input
                                type="number"
                                min={15}
                                max={10080}
                                value={draft.handoffNudgeMinutes}
                                onChange={(event) => setDraft((current) => ({ ...current, handoffNudgeMinutes: event.target.value }))}
                                className={`w-20 rounded border px-2 py-1 text-xs ${inputCls}`}
                                aria-label="Handoff nudge minutes"
                            />
                        </label>
                    </SettingsSection>
                    <SettingsSection
                        title="Thread list order"
                        dark={dark}
                        help={<HoverHelp helpId="communications.thread-order" userId={userId} />}
                    >
                        <label className={rowCls}>
                            <span>Group by status</span>
                            <input
                                type="checkbox"
                                checked={draft.groupThreadsByStatus}
                                disabled={saving}
                                onChange={(event) => setDraft((current) => ({ ...current, groupThreadsByStatus: event.target.checked }))}
                                aria-label="Group by status"
                            />
                        </label>
                        {draft.groupThreadsByStatus && (
                            <>
                                <div className={`space-y-2 text-sm ${labelCls}`}>
                                    <div className="font-medium">Status category priority</div>
                                    <div className="space-y-1" aria-label="Status category priority order">
                                        {draft.statusCategoryOrder.map((status, index) => (
                                            <div
                                                key={status}
                                                draggable
                                                onDragStart={() => setDraggedStatus(status)}
                                                onDragOver={(event) => event.preventDefault()}
                                                onDrop={() => dropStatus(status)}
                                                className={`flex items-center justify-between gap-2 rounded-lg border px-2 py-1.5 ${dark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}
                                            >
                                                <span className="cursor-grab text-xs" aria-hidden="true">☰</span>
                                                <span className="flex-1">{STATUS_CATEGORY_LABELS[status] || status}</span>
                                                <span className={`text-xs ${hintCls}`}>#{index + 1}</span>
                                                <button type="button" onClick={() => moveStatus(status, -1)} className={segmentBase} aria-label={`Move ${STATUS_CATEGORY_LABELS[status] || status} up`}>↑</button>
                                                <button type="button" onClick={() => moveStatus(status, 1)} className={segmentBase} aria-label={`Move ${STATUS_CATEGORY_LABELS[status] || status} down`}>↓</button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className={rowCls}>
                                    <span>Reorder within status group</span>
                                    <span className="flex items-center gap-1.5">
                                        {[
                                            ['drag_drop', 'Drag & drop'],
                                            ['numbered', 'Numbered'],
                                        ].map(([value, label]) => (
                                            <button
                                                key={value}
                                                type="button"
                                                onClick={() => setDraft((current) => ({ ...current, threadReorderMode: value }))}
                                                className={`${segmentBase} ${draft.threadReorderMode === value ? segmentActive : ''}`}
                                                aria-pressed={draft.threadReorderMode === value}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </span>
                                </div>
                                <div className={rowCls}>
                                    <span>Pinned threads</span>
                                    <span className="flex items-center gap-1.5">
                                        {[
                                            ['top', 'Top of group'],
                                            ['bottom', 'Bottom of group'],
                                        ].map(([value, label]) => (
                                            <label
                                                key={value}
                                                className={`${segmentBase} ${draft.pinnedThreadPosition === value ? segmentActive : ''}`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="pinned-thread-position"
                                                    value={value}
                                                    checked={draft.pinnedThreadPosition === value}
                                                    onChange={(event) => setDraft((current) => ({ ...current, pinnedThreadPosition: event.target.value }))}
                                                    className="sr-only"
                                                />
                                                {label}
                                            </label>
                                        ))}
                                    </span>
                                </div>
                            </>
                        )}
                    </SettingsSection>
                    {groupSettingsClient && orgId && (
                        <SettingsSection title="Group communications" dark={dark}>
                            <GroupCommunicationsSettings
                                client={groupSettingsClient}
                                orgId={orgId}
                                canChange={canChangeGroupSettings}
                                theme={theme}
                                onSaved={onGroupSettingsSaved}
                            />
                        </SettingsSection>
                    )}
                    {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
                </div>
                <div className={`shrink-0 flex justify-end gap-2 border-t px-4 py-3 ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <button
                        type="button"
                        onClick={onClose}
                        className={`rounded-lg border px-3 py-1.5 text-sm ${dark ? 'border-gray-600 text-gray-200 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                        Cancel
                    </button>
                    <SupportWriteGuard mode="disable" tooltip="Saving Communications settings is disabled in a read-only support session">
                        <button
                            type="button"
                            disabled={saving}
                            onClick={() => onSave?.(draft)}
                            className="rounded-lg bg-[var(--btn-fill)] px-3 py-1.5 text-sm font-medium text-[var(--btn-ink)] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {saving ? 'Saving…' : 'Save settings'}
                        </button>
                    </SupportWriteGuard>
                </div>
            </div>
        </div>
    );
}

export default CommunicationsSettingsModal;
